"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import AppButton from "./AppButton";
import Container from "../wrapper/Container";
import BookingEmbedCard from "../common/cards/BookingEmbedCard";
import SectionSubHeading from "../common/titles/SectionSubHeading";
import SectionTitle from "../common/titles/SectionTitle";
import TextMarquee from "../animations/TextMarquee";
import { rise, word, wordStagger } from "@/lib/animation/variant";
import { useCalBooking } from "@/providers/booking/CalBookingProvider";
import { BOOKING_SECTION_ID } from "@/lib/constant";
import { MARQUEE_TAGS, TITLE_LINES } from "@/data/BookSessionData";
const BookSession = () => {
  const $ = _c(19);
  const {
    enabled
  } = useCalBooking();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-96 bg-linear-to-b from-primary/25 via-primary/10 to-transparent" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <motion.div aria-hidden={true} className="pointer-events-none absolute -right-24 bottom-0 -z-10 size-80 rounded-full bg-primary/15 blur-3xl" animate={{
      x: [0, -30, 0],
      y: [0, -40, 0],
      scale: [1, 1.2, 1]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      once: true,
      amount: 0.4
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <motion.div variants={rise} initial="hidden" whileInView="show" viewport={t2} custom={0}><SectionSubHeading subtitle="Book a Session" /></motion.div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      once: true,
      amount: 0.4
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SectionTitle className="font-black" title={<motion.span variants={wordStagger} initial="hidden" whileInView="show" viewport={t4} className="block">{TITLE_LINES.map(_temp)}</motion.span>} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <motion.p variants={rise} initial="hidden" whileInView="show" viewport={{
      once: true,
      amount: 0.4
    }} custom={1} className="max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">Skip the back-and-forth emails. Pick a time and let's talk through your idea, your goals, and how we can help you get there — no strings attached.</motion.p>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      once: true,
      amount: 0.4
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== enabled) {
    t8 = !enabled && <AppButton className="w-64" buttonClass="h-12" href="/contact"><span>Book a Session</span><ArrowUpRight size={18} /></AppButton>;
    $[8] = enabled;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">No commitment · Free 30-min intro</span>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8) {
    t10 = <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">{t3}{t5}{t6}<motion.div variants={rise} initial="hidden" whileInView="show" viewport={t7} custom={2} className="flex flex-col items-center gap-3">{t8}{t9}</motion.div></div>;
    $[11] = t8;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="mt-10 md:mt-14"><BookingEmbedCard /></div>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10) {
    t12 = <Container className="px-4 lg:px-10">{t10}{t11}</Container>;
    $[14] = t10;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <TextMarquee items={MARQUEE_TAGS} className="mt-16 md:mt-20 lg:mt-4" />;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t12) {
    t14 = <section id={BOOKING_SECTION_ID} className="relative isolate w-full overflow-hidden bg-white py-20 md:py-28 lg:py-24">{t0}{t1}{t12}{t13}</section>;
    $[17] = t12;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  return t14;
};
export default BookSession;
function _temp(line, li) {
  return <span key={li} className="block overflow-hidden">{line.map((w, wi) => <motion.span key={`${li}-${wi}`} variants={word} className="mr-[0.25em] inline-block">{w}</motion.span>)}</span>;
}